// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {Plural, Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

interface RateLimitedConfirmModalProps {
	title: string;
	retryAfter?: number;
	onRetry?: () => void;
}
export const RateLimitedConfirmModal = observer(({title, retryAfter, onRetry}: RateLimitedConfirmModalProps) => {
	const {t} = useLingui();
	const hasRetryAfter = retryAfter != null;
	const renderRateLimitTime = (totalSeconds: number): React.ReactNode => {
		if (totalSeconds < 60) {
			return <Plural value={totalSeconds} one="# second" other="# seconds" />;
		}
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		if (seconds === 0) {
			return <Plural value={minutes} one="# minute" other="# minutes" />;
		}
		return (
			<Trans>
				<Plural value={minutes} one="# minute" other="# minutes" /> and{' '}
				<Plural value={seconds} one="# second" other="# seconds" />
			</Trans>
		);
	};
	return (
		<ConfirmModal
			title={title}
			description={
				hasRetryAfter ? (
					<Trans>You're being rate limited. Please wait {renderRateLimitTime(retryAfter)} before trying again.</Trans>
				) : (
					t`The problem with being faster than light is that you can only live in darkness. Take a breather and try again.`
				)
			}
			secondaryText={hasRetryAfter ? t`Retry` : t`Gotcha`}
			onSecondary={onRetry}
		/>
	);
});
